import type { MandatesModel } from "@/src/types/mandatesModel"
import type { FormSchemaJobPostingType } from "../schemas/jobPostingFormSchema"

type JobClientSource = {
    clientId?: string | null
    client?: { id?: string; value?: string } | null
}

/** Read nested `client.id` from API responses; fall back to legacy flat `clientId`. */
export function resolveClientId(source: JobClientSource | null | undefined): string {
    if (!source) {
        return ""
    }

    const nestedClientId = source.client?.id ?? source.client?.value
    if (nestedClientId?.trim()) {
        return nestedClientId.trim()
    }

    const legacyClientId = source.clientId
    if (typeof legacyClientId === "string" && legacyClientId.trim()) {
        return legacyClientId.trim()
    }

    return ""
}

/** Resolve clientId for PUT/POST payloads, checking multiple sources in order. */
export function resolveClientIdForApiPayload(
    ...sources: Array<JobClientSource | null | undefined>
): string {
    for (const source of sources) {
        const resolvedClientId = resolveClientId(source)
        if (resolvedClientId) {
            return resolvedClientId
        }
    }

    return ""
}

export const mapJobPostingToForm = (mandate: MandatesModel): FormSchemaJobPostingType => {
    return {
        jobDescriptionPrompt: (mandate as any).jobDescription ?? mandate.jobDescriptionPrompt ?? "",
        jdFile: null,
        jdFileLink: mandate.jdFileLink ?? (mandate as any).jobDescriptionUrl ?? null,
        clientId: resolveClientId(mandate),
        title: mandate.title ?? (mandate as any).displayName ?? "",
        jobCity: mandate.jobCity ?? "",
        jobArea: mandate.jobArea ?? "",
        experienceRequired: mandate.experienceRequired ?? "ANY",
        experiencedCandidateDefinition: mandate.experiencedCandidateDefinition ?? "",
        fresherCandidateDefinition: mandate.fresherCandidateDefinition ?? "",
        workType: mandate.workType ?? "",
        workMode: mandate.workMode ?? "",
        workScheduleAndShiftDetails: mandate.workScheduleAndShiftDetails ?? "",
        experiencedCompensationDetails: mandate.experiencedCompensationDetails ?? "",
        freshersCompensationDetails: mandate.freshersCompensationDetails ?? "",
        additionalDetails: mandate.additionalDetails ?? "",
        interviewProcess: mandate.interviewProcess ?? "",
        rounds: mandate.rounds ?? [],
        status: mandate.status ?? "DRAFT",
        agentFriendlyJobDescription: (mandate as any).agentFriendlyJobDescription ?? "",
        roundCost: (mandate as any).roundCost ?? 1,
        roundMetadataId: (mandate as any).roundMetadataId ?? null,
        companyName: (mandate as any).companyName ?? undefined,
        candidateFaqPairs: (mandate as any).candidateFaq?.candidateFaqs ?? [],
        candidateFaqFileUrl: (mandate as any).candidateFaq?.candidateFaqUrl ?? undefined,
        candidateFaqFileName: (mandate as any).candidateFaq?.candidateFaqUrl
            ? (mandate as any).candidateFaq.candidateFaqUrl.split("/").pop()?.split("?")[0]
            : undefined,
    }
}

// Backwards compatibility alias
export const mapMandateToForm = mapJobPostingToForm
